import React, { useEffect, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import '../../../component-styles/PublicProposalPage.css';
import API_BASE from '../../../utils/api.js';
import { documentLabel } from '../../../utils/documentTitle.js';
import { isWillCallDestination } from '../../../utils/freightDestinations.js';

const ERROR_MESSAGES = {
  expired:
    'This purchase order link has expired or been revoked. Please reach out to your designer to request a new one.',
  not_found:
    'This purchase order link is no longer active. Please contact your designer for an updated link.',
  network:
    "We couldn't load this purchase order right now. Please try refreshing the page, or contact your designer if the problem continues.",
};

function formatDate(d) {
  if (!d) return '—';
  const date = new Date(d);
  return Number.isNaN(date.getTime()) ? '—' : date.toLocaleDateString();
}

function fmtMoney(v) {
  const n = Number(v);
  if (!Number.isFinite(n)) return '$0.00';
  return n.toLocaleString(undefined, { style: 'currency', currency: 'USD' });
}

export default function PublicPurchaseOrderPage({ variant }) {
  const { shareToken: routeShareToken } = useParams();
  const location = useLocation();

  const [po, setPo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const queryParam = variant === 'vendor' ? 'publicPOVendor' : 'publicPOClient';
  const shareToken = routeShareToken || new URLSearchParams(location.search).get(queryParam);

  useEffect(() => {
    if (!shareToken) return;

    (async () => {
      try {
        const res = await fetch(
          `${API_BASE}/api/purchase-orders/public/${variant}/${encodeURIComponent(shareToken)}`
        );
        if (res.status === 410) throw new Error('expired');
        if (res.status === 404) throw new Error('not_found');
        // Anything else is our side failing, not a dead link — telling a vendor
        // the link is no longer active sends them to the designer for a new one
        // that would break the same way.
        if (!res.ok) throw new Error('network');
        setPo(await res.json());
      } catch (err) {
        setError(err.message || 'network');
      } finally {
        setLoading(false);
      }
    })();
  }, [shareToken, variant]);

  useEffect(() => {
    if (!po) return;
    document.title = po.orderNumber ? `Purchase Order #${po.orderNumber}` : 'Purchase Order';
    return () => {
      document.title = 'InteriorFolio';
    };
  }, [po]);

  if (loading) return <div className="public-loading">Loading purchase order…</div>;
  if (error)
    return (
      <div className="public-error">
        {ERROR_MESSAGES[error] || ERROR_MESSAGES.network}
      </div>
    );
  if (!po) return null;

  return (
    <div className="public-proposal-wrapper">
      <header className="public-topbar">
        <span className="public-firm-name">
          {variant === 'vendor' ? po.billToName || 'Purchase Order' : po.vendorName || 'Purchase Order'}
        </span>
      </header>

      <div className="public-layout">
        <div className="public-content">
          <div className="public-proposal-card">
            {variant === 'vendor' ? (
              <VendorPurchaseOrderView po={po} />
            ) : (
              <ClientPurchaseOrderView po={po} />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function VendorPurchaseOrderView({ po }) {
  const items = Array.isArray(po.items) ? po.items : [];

  return (
    <>
      <div className="public-header">
        <h1>{documentLabel(po.orderNumber, po.title) || 'Purchase Order'}</h1>
        <span className={`public-status status-${(po.status || 'draft').toLowerCase().replace(/\s+/g, '_')}`}>
          {po.status || 'No status'}
        </span>
        <p className="proposal-id">Date: {formatDate(po.date)}</p>
      </div>

      <div className="public-info-grid">
        <div>
          <strong>Bill To</strong>
          <p>
            {po.billToName || '—'}<br />
            {po.billToStreet}{po.billToStreet ? <br /> : null}
            {[po.billToCity, po.billToState, po.billToZip].filter(Boolean).join(', ')}<br />
            {po.billToEmail}
          </p>
        </div>
        <div>
          <strong>Ship To</strong>
          {isWillCallDestination(po.shipToType) ? (
            <p>Will Call</p>
          ) : (
            <p>
              {po.shipCompany || '—'}<br />
              {po.shipStreet}{po.shipStreet ? <br /> : null}
              {[po.shipCity, po.shipState, po.shipZip].filter(Boolean).join(', ')}
            </p>
          )}
        </div>
        {po.sidemark && (
          <div>
            <strong>Sidemark</strong>
            <p>{po.sidemark}</p>
          </div>
        )}
        {po.vendorPaymentTerms && (
          <div>
            <strong>Payment Terms</strong>
            <p>{po.vendorPaymentTerms}</p>
          </div>
        )}
      </div>

      <div className="public-items-table">
        <table className="proposal-table">
          <thead>
            <tr>
              <th>Item</th>
              <th className="col-qty">Qty</th>
              <th className="col-rate">Rate</th>
              <th className="col-total">Total</th>
            </tr>
          </thead>
          <tbody>
            {items.map((item, i) => (
              <tr key={item.itemId || i}>
                <td>
                  <div className="item-block">
                    {item.imageUrl && <img className="item-img" src={item.imageUrl} alt="" />}
                    <div className="item-info">
                      <div className="item-title">{item.name || '—'}</div>
                      {item.room && <div className="item-room">{item.room}</div>}
                      {item.description && <div className="item-description">{item.description}</div>}
                    </div>
                  </div>
                </td>
                <td className="col-qty">{item.quantity} {item.unitType && item.unitType !== 'Each' ? item.unitType : ''}</td>
                <td className="col-rate">{fmtMoney(item.rate)}</td>
                <td className="col-total grand-cell">{fmtMoney(Number(item.rate || 0) * Number(item.quantity || 0))}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="public-totals">
        <p><span>Subtotal</span><span>{fmtMoney(po.subtotal)}</span></p>
        {Number(po.freight) > 0 && <p><span>Freight</span><span>{fmtMoney(po.freight)}</span></p>}
        {Number(po.taxes) > 0 && <p><span>Tax</span><span>{fmtMoney(po.taxes)}</span></p>}
        <p className="grand">Total: {fmtMoney(po.total)}</p>
      </div>

      {po.notes && (
        <div className="public-terms">
          <strong>Notes</strong>
          <p>{po.notes}</p>
        </div>
      )}

      <PurchaseOrderAttachmentsList attachments={po.attachments} />
    </>
  );
}

function PurchaseOrderAttachmentsList({ attachments }) {
  const list = Array.isArray(attachments) ? attachments : [];
  if (!list.length) return null;

  return (
    <div className="public-terms">
      <strong>Attachments</strong>
      <ul style={{ margin: '8px 0 0', paddingLeft: 18 }}>
        {list.map((a) => (
          <li key={a.id}>
            <a href={a.url} target="_blank" rel="noreferrer">
              {a.name}
            </a>
          </li>
        ))}
      </ul>
    </div>
  );
}

const PO_STATUS_ORDER = ['Ordered', 'Back Ordered', 'Workroom', 'En route', 'Delivered', 'Installed'];

function isStepReached(status, step) {
  const idx = PO_STATUS_ORDER.indexOf(status);
  const stepIdx = PO_STATUS_ORDER.indexOf(step);
  return idx >= 0 && stepIdx >= 0 && idx >= stepIdx;
}

function ClientPurchaseOrderView({ po }) {
  const items = Array.isArray(po.items) ? po.items : [];
  const hasImages = items.some((it) => it.imageUrl);

  return (
    <>
      <div className="public-header">
        <h1>{documentLabel(po.orderNumber, po.title) || 'Purchase Order'}</h1>
        <span className={`public-status status-${(po.status || 'draft').toLowerCase().replace(/\s+/g, '_')}`}>
          {po.status || 'No status'}
        </span>
        <p className="proposal-id">
          {po.vendorName ? `Vendor: ${po.vendorName} · ` : ''}Date: {formatDate(po.date)}
        </p>
      </div>

      {po.notes && (
        <div className="public-terms">
          <p style={{ margin: 0 }}>{po.notes}</p>
        </div>
      )}

      <PurchaseOrderAttachmentsList attachments={po.attachments} />

      <div style={{ padding: '8px 40px 32px' }}>
        {items.length === 0 && <p style={{ color: '#6b7280' }}>No items on this order.</p>}
        {items.map((item, i) => (
          <div key={item.itemId || i} className="item-block" style={{ padding: '16px 0', borderBottom: '1px solid #f3f4f6' }}>
            {hasImages && (item.imageUrl ? <img className="item-img" src={item.imageUrl} alt="" /> : <div className="item-img" />)}
            <div className="item-info" style={{ flex: 1 }}>
              <div className="item-title">{item.name || '—'}</div>
              {item.description && <div className="item-description">{item.description}</div>}
              <div className="item-vendor">
                {[item.room && `Room: ${item.room}`, item.quantity != null && `Qty: ${item.quantity}`]
                  .filter(Boolean)
                  .join(' · ')}
              </div>
              <div style={{ display: 'flex', gap: 4, marginTop: 8 }}>
                {PO_STATUS_ORDER.map((step) => (
                  <div
                    key={step}
                    title={step}
                    style={{
                      flex: 1,
                      height: 4,
                      borderRadius: 2,
                      background: isStepReached(item.status, step) ? '#62794e' : '#e5e7eb',
                    }}
                  />
                ))}
              </div>
              <div style={{ fontSize: '0.72rem', color: '#6b7280', marginTop: 4 }}>{item.status || 'No status'}</div>
            </div>
          </div>
        ))}
      </div>
    </>
  );
}
